import React, { useState, useEffect, useRef } from "react";

import { Button, Radio } from "antd";
import Modal from "antd/es/modal/Modal";

import { ReactComponent as AlertLineIcon } from "../../../../../assets/images/product/specmanager/alert-line.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../../assets/images/product/specmanager/arrow-right-line.svg";
import { ReactComponent as CloseLineIcon } from "../../../../../assets/images/product/specmanager/close-line.svg";
import { ReactComponent as InformationLineIcon } from "../../../../../assets/images/product/specmanager/information-line.svg";

import "./ProductSpecificSpecsModal.css";

const STANDARD_APPLIES_MESSAGE = "Updating the spec will apply the new value to products following the standard spec.";

const SPEC_APPLIES_ALL_SELECTED_MESSAGE = "Adding specs will update the status of all selected products linked to this test. This cannot be undone.";

const BULLETS_MIXED = [
  "Some of the selected products have product-specific limits defined.",
  STANDARD_APPLIES_MESSAGE,
  "Products with product-specific specs will remain unchanged unless you choose to override them.",
];

const MIXED_RADIOS = [
  {
    value: "standard",
    heading: "Apply to standard spec products only",
    subheading: "Custom (Product-specific) limits remain unchanged.",
    disableWhenNoStandard: true,
  },
  {
    value: "all",
    heading: "Apply to all selected products",
    subheading: "Override custom (product-specific) specs.",
    disableWhenNoStandard: false,
  },
];

function BulletList({ items, listClassName = "ProductSpecificSpecsModal__Bullets" }) {
  return (
    <ul className={listClassName}>
      {items.map((text, i) => (
        <li key={i}>{text}</li>
      ))}
    </ul>
  );
}

function CustomSpecCountBanner({ count }) {
  return (
    <div className="ProductSpecificSpecsModal__BottomBanner">
      <InformationLineIcon className="ProductSpecificSpecsModal__BottomBannerIcon" />
      <div className="ProductSpecificSpecsModal__BottomBannerText">
        <span className="ProductSpecificSpecsModal__BottomBannerMain">{count}</span>
        <span> Products have product-specific spec.</span>
      </div>
    </div>
  );
}

function ProductSpecsModalHeader({
  loading,
  onClose,
  title,
  showIcon,
}) {
  return (
    <div className="ProductSpecificSpecsModal__Header">
      {showIcon ? <AlertLineIcon className="ProductSpecificSpecsModal__HeaderIcon" /> : null}
      <div className="ProductSpecificSpecsModal__HeaderTitle">{title}</div>
      <Button
        htmlType="button"
        className="ProductSpecificSpecsModal__CloseBtn"
        aria-label="Close"
        onClick={() => !loading && onClose?.()}
        disabled={loading}
      >
        <CloseLineIcon className="ProductSpecificSpecsModal__CloseIcon" aria-hidden />
      </Button>
    </div>
  );
}

function StandardOnlyBody() {
  return (
    <div className="ProductSpecificSpecsModal__BodyText">
      {SPEC_APPLIES_ALL_SELECTED_MESSAGE}
    </div>
  );
}

function CustomOnlyBody({ customProductsCount }) {
  return (
    <>
      <div className="ProductSpecificSpecsModal__BodyText">
        {SPEC_APPLIES_ALL_SELECTED_MESSAGE}
      </div>
      <CustomSpecCountBanner count={customProductsCount} />
    </>
  );
}

function MixedBody({
  scope,
  setScope,
  canApplyStandard,
  customProductsCount,
}) {
  return (
    <>
      <div className="ProductSpecificSpecsModal__BodyText">
        <BulletList items={BULLETS_MIXED} />
        <div className="ProductSpecificSpecsModal__ProceedText">How would you like to proceed?</div>
      </div>
      <Radio.Group
        className="ProductSpecificSpecsModal__RadioGroup"
        value={scope}
        onChange={(e) => setScope(e.target.value)}
      >
        {MIXED_RADIOS.map((radioOption) => {
          const isDisabled = radioOption.disableWhenNoStandard && !canApplyStandard;
          const isSelected = scope === radioOption.value;
          return (
            <div
              key={radioOption.value}
              className={[
                "ProductSpecificSpecsModal__RadioItem",
                isSelected ? "ProductSpecificSpecsModal__RadioItem--active" : "",
                isDisabled ? "ProductSpecificSpecsModal__RadioItem--disabled" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => {
                if (!isDisabled) setScope(radioOption.value);
              }}
            >
              <Radio value={radioOption.value} disabled={isDisabled} />
              <div className="ProductSpecificSpecsModal__RadioText">
                <div className="ProductSpecificSpecsModal__RadioHeading">{radioOption.heading}</div>
                <div className="ProductSpecificSpecsModal__RadioSubheading">{radioOption.subheading}</div>
              </div>
            </div>
          );
        })}
      </Radio.Group>
      <CustomSpecCountBanner count={customProductsCount} />
    </>
  );
}

/**
 * @param {"standardOnly"|"customOnly"|"mixed"} variant
 * @param {"add"|"update"} [testSpecSaveAction]
 */
export default function ProductSpecificSpecsModal({
  open,
  onClose,
  variant = "mixed",
  customProductsCount = 0,
  standardProductsCount = 0,
  onApplyStandardOnly,
  onApplyAllSelected,
  onSaveAllStandardProducts,
  loading = false,
  testSpecSaveAction = "update",
}) {
  const canApplyStandard = standardProductsCount > 0;
  const [scope, setScope] = useState(() => (canApplyStandard ? "standard" : "all"));
  const saveClickLockRef = useRef(false);

  useEffect(() => {
    if (open && variant === "mixed") {
      setScope(standardProductsCount > 0 ? "standard" : "all");
    }
  }, [open, variant, standardProductsCount]);

  useEffect(() => {
    if (!open) saveClickLockRef.current = false;
  }, [open]);

  const isStandardOnly = variant === "standardOnly";
  const isCustomOnly = variant === "customOnly";

  const standardHeaderTitle = testSpecSaveAction === "add" ? "Add Specs" : "Update Specs";
  const standardPrimaryLabel = testSpecSaveAction === "add" ? "Save Spec" : "Save Specs";

  const handleSave = async () => {
    if (loading || saveClickLockRef.current) return;
    saveClickLockRef.current = true;
    try {
      if (isStandardOnly) {
        await onSaveAllStandardProducts?.();
        return;
      }
      if (isCustomOnly) {
        await onApplyAllSelected?.();
        return;
      }
      const apply = scope === "standard" ? onApplyStandardOnly : onApplyAllSelected;
      await apply?.();
    } finally {
      saveClickLockRef.current = false;
    }
  };

  let body;
  if (isStandardOnly) {
    body = <StandardOnlyBody />;
  } else if (isCustomOnly) {
    body = <CustomOnlyBody customProductsCount={customProductsCount} />;
  } else {
    body = (
      <MixedBody
        scope={scope}
        setScope={setScope}
        canApplyStandard={canApplyStandard}
        customProductsCount={customProductsCount}
      />
    );
  }

  return (
    <Modal
      open={open}
      maskClosable={false}
      closable={false}
      centered
      footer={null}
      width={540}
      className="ProductSpecificSpecsModal"
      wrapClassName="ProductSpecificSpecsModal__Wrap"
    >
      <div className="ProductSpecificSpecsModal__Main">
        <ProductSpecsModalHeader
          loading={loading}
          onClose={onClose}
          title={isStandardOnly ? standardHeaderTitle : "Product-specific specs detected"}
          showIcon={!isStandardOnly}
        />

        <div className="ProductSpecificSpecsModal__Body">{body}</div>

        <div className="ProductSpecificSpecsModal__Footer">
          <Button
            type="default"
            disabled={loading}
            onClick={() => onClose?.()}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={loading}
            loading={loading}
            onClick={handleSave}
            icon={<ArrowRightIcon />}
          >
            {isStandardOnly ? standardPrimaryLabel : "Save Spec"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
